import React, { FC, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { UiDatePicker } from '@ru-digital-assets/ui-component';
import { useFormik } from 'formik';
import FormFooter from '../../../containers/form-footer';
import AppCheckbox from '../../../modules/app-checkbox/app-checkbox';
import AppPageMain from '../../../modules/app-page/app-page--main';
import IconBtn from '../../../modules/icon-btn/icon-btn';
import ModuleTitleText from '../../../modules/module-title-text/module-title-text';
import PageHeader from '../../../modules/page-header';
import Step from '../../../modules/step';
import TableHeaderWrapper from '../../../modules/table-header-wrapper/table-header-wrapper';
import TableWrapper from '../../../modules/table-wrapper/table-wrapper';
import UiInput from '../../../modules/ui/ui-input/ui-input';
import UiSelect, { OptionType } from '../../../modules/ui/ui-select/ui-select';
import UiTextarea from '../../../modules/ui/ui-textarea/ui-textarea';
import { useAppDispatch } from '../../../store';
import { releaseCfa } from '../../../store/actions/operations-sign';
import { RootState } from '../../../types';
import currencyFormat from '../../../utils/currencyFormat';
import { ReleaseDocumentIcon, CloseIcon } from 'assets/icons';
import './index.scss';
import { getPaymentTokens } from '../../../store/actions/common-info';

type FormStateType = {
  claim?: string;
  companyId: number;
  companyName?: string;
  paymentType?: string;
  paymentCount?: number;
  accountId: number;
  // minDate?: Date;
  expiryDate?: Date;
  provisionText?: string;
  liabilityText?: string;
  comment?: string;
  isProvision?: boolean;
  isLiability?: boolean;
  tokenCount?: number;
  isFiat: boolean;
  tokenPrice?: number;
  publicRelease: boolean;
};

const initialFormState: FormStateType = {
  accountId: 2,
  companyId: 0,
  isFiat: true,
  paymentType: 'FIAT',
  publicRelease: false,
};

const CfaReleasePage: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const [loadingBtn, setLoadingBtn] = useState(false);
  const [tokenCount, setTokenCount] = useState<number>();
  const [tokenPrice, setTokenPrice] = useState<number>();
  const [paymentTokenLimit, setPaymentTokenLimit] = useState<number>();
  const [validPaymentCount, setValidPaymentCount] = useState(false);

  const companyInfos = useSelector(
    (state: RootState) => state.companiesInfo.companies
  );

  const paymentTypes = useSelector(
    (state: RootState) => state.commonInfo.paymentTokens
  );

  const accountId = useSelector((state: RootState) => state.auth.account.id);

  let paymentOptionTypes = paymentTypes.map((item) => {
    const opt: OptionType = {
      value: item.code,
      label: item.name,
      limit: item.totalCount,
    };
    return opt;
  });

  const companies: OptionType[] = useMemo(() => {
    const arr = [];
    for (let key in companyInfos) {
      if (accountId.toString() !== key) {
        arr.push({
          value: key,
          label: companyInfos[key].shortName,
        });
      }
    }
    return arr;
  }, [companyInfos]);

  const startDate = new Date(new Date().getTime() + 1000 * 60 * 60 * 24);

  const formState = useFormik({
    initialValues: initialFormState,
    onSubmit: (values) => {
      if (values.claim || values.paymentCount) {
        setLoadingBtn(true);
        dispatch(
          releaseCfa({
            claim:
              (values.paymentType === 'FIAT' || !values.paymentType) &&
              values.claim
                ? Number(values.claim.replace(/\s/g, ''))
                : Number(claim),
            companyId: values.companyId,
            paymentType: values.paymentType || 'FIAT',
            paymentCount: values.paymentCount
              ? Number(values.paymentCount)
              : undefined,
            tokenCount: values.tokenCount || 1,
            tokenPrice: values.tokenPrice || 100,
            provisionText: values.isProvision ? values.provisionText : '',
            liabilityText: values.isLiability ? values.liabilityText : '',
            commentText: values.comment,
            // minDate: values.minDate,
            expiryDate: values.expiryDate,
            publicRelease: values.publicRelease,
          })
        ).then(() => history.push('/cfa/sign'));
      }
    },
  });

  const claim = useMemo(
    () => tokenCount && tokenPrice && tokenCount * tokenPrice,
    [tokenCount, tokenPrice]
  );

  useEffect(() => {
    dispatch(getPaymentTokens());
  }, []);

  return (
    <AppPageMain>
      <div className="container">
        <div className="row">
          <div className="col-19 col-lg-27 col-xl-35">
            <PageHeader
              items={[
                { link: '/assets', title: 'Активы' },
                { link: '#', title: 'Выпуск ЦФА' },
              ]}
              iconType="assets"
            />
          </div>
        </div>
        <div className="row cfa-release">
          <div className="col-19 col-lg-27 col-xl-35">
            <TableWrapper>
              <div className="assets">
                <div className="assets__header">
                  <TableHeaderWrapper
                    leftContent="Выпуск цифровых финансовых активов"
                    onCloseClick={() => history.replace('/assets')}
                  />
                </div>
                <div className="assets__body">
                  <div className="row">
                    <form className="form-assets">
                      <div className="form-assets__item">
                        <Step
                          steps={[
                            { title: 'Параметры', active: true },
                            { title: 'Подпись' },
                            { title: 'Согласование' },
                            { title: 'Выпуск' },
                          ]}
                        />
                      </div>

                      <div className="form-assets__item">
                        <p className="p p_font-size_14 mb_8">
                          Укажите для кого выпускаются цифровые финансовые
                          активы (токены)
                        </p>
                        {!formState.values.publicRelease && (
                          <div className="form-item">
                            <UiSelect
                              placeholder="Укажите Приобретателя"
                              options={companies}
                              value={{
                                value: formState.values.companyId,
                                label: formState.values.companyName,
                              }}
                              title={formState.values.companyName}
                              additionalInfo={
                                formState.values.companyId && (
                                  <div className="cfa-release__selected-company-inn">
                                    <p>
                                      ИНН{' '}
                                      {
                                        companyInfos[formState.values.companyId]
                                          .tin
                                      }
                                    </p>
                                    <CloseIcon
                                      className="cfa-release__selected-company-clear"
                                      onClick={() => {
                                        formState.setFieldValue(
                                          'companyId',
                                          ''
                                        );
                                        formState.setFieldValue(
                                          'companyName',
                                          ''
                                        );
                                      }}
                                    />
                                  </div>
                                )
                              }
                              onChange={(val) => {
                                if (val) {
                                  formState.setFieldValue(
                                    'companyId',
                                    Number((val as OptionType)?.value)
                                  );
                                  formState.setFieldValue(
                                    'companyName',
                                    (val as OptionType)?.label
                                  );
                                }
                              }}
                            />
                          </div>
                        )}
                      </div>
                      <label className="form-label">
                        <AppCheckbox
                          onChangeValue={(val) => {
                            formState.setFieldValue('publicRelease', val);
                          }}
                        />
                        Публичный выпуск
                      </label>

                      <div className="form-assets__item flex">
                        <UiInput
                          required
                          rightText="шт"
                          name="tokenCount"
                          className="cfa-release__input"
                          value={tokenCount && tokenCount.toLocaleString('ru')}
                          onChange={(
                            e: React.ChangeEvent<HTMLInputElement>
                          ) => {
                            let number = Number(
                              e.target.value.replace(/\s/g, '')
                            );
                            setTokenCount(number);
                            formState.setFieldValue('tokenCount', number);
                          }}
                          lg
                          label="Количество выпускаемых токенов"
                        />
                        X
                        <UiInput
                          required
                          rightText="₽"
                          name="tokenPrice"
                          className="cfa-release__input"
                          currency
                          value={tokenPrice}
                          onChange={(value: any) => {
                            setTokenPrice(value);
                            formState.setFieldValue('tokenPrice', value);
                          }}
                          lg
                          label="Денежное требование по токену"
                        />
                      </div>

                      <div className="form-assets__item form-assets__item__200">
                        <UiInput
                          readOnly
                          className={claim ? 'input--completed' : ''}
                          value={claim && claim.toLocaleString('ru')}
                          rightText="₽"
                          name="claim"
                          lg
                          // label="Сумма для оплаты"
                          label="Общий объем денежного требования"
                        />
                      </div>

                      <div className="form-assets__item">
                        <p className="text">Срок исполнения обязательств</p>
                        <div className="form-item">
                          <UiDatePicker
                            selected={formState.values.expiryDate}
                            minDate={startDate}
                            dateFormat="dd.MM.yyyy"
                            onChange={(date: Date) => {
                              date.setHours(date.getHours() + 3);
                              formState.setFieldValue('expiryDate', date);
                            }}
                          />
                        </div>
                      </div>
                      <div className="form-assets__item">
                        <p className="text h3">
                          <strong>Укажите способ и сумму оплаты</strong>
                        </p>
                      </div>
                      <div className="form-assets__item">
                        <div className="form-item flex">
                          <div className="radio-btn-select">
                            <input
                              type="radio"
                              value="FIAT"
                              checked={formState.values.isFiat}
                              onClick={() => {
                                formState.setFieldValue('isFiat', true);
                                setPaymentTokenLimit(undefined);
                                formState.setFieldValue('paymentType', 'FIAT');
                              }}
                            />
                            <span className="text h3">
                              Оплата денежными средствами
                            </span>
                          </div>
                          <div className="radio-btn-select">
                            <input
                              type="radio"
                              value="TOKENS"
                              checked={!formState.values.isFiat}
                              onClick={() => {
                                formState.setFieldValue('isFiat', false);
                              }}
                            />
                            <span className="text h3">
                              Передача иных токенов
                            </span>
                          </div>
                        </div>
                      </div>

                      {formState.values.isFiat &&
                        (formState.values.paymentType === 'FIAT' ||
                          formState.values.paymentType === undefined) && (
                          <div className="form-assets__item form-assets__item__200">
                            <UiInput
                              required
                              rightText="₽"
                              name="claim"
                              className="cfa-release__input"
                              currency
                              onChange={(value: any) => {
                                formState.setFieldValue('claim', value);
                              }}
                              lg
                              label="Сумма для оплаты"
                            />
                          </div>
                        )}

                      {!formState.values.isFiat && (
                        <>
                          <div className="form-assets__item">
                            <UiSelect
                              placeholder="Выберите токен"
                              options={paymentOptionTypes}
                              onChange={(val) => {
                                let value = (val as OptionType)?.value;
                                if (value !== undefined && value !== 'FIAT') {
                                  let limit = (val as OptionType)?.limit;
                                  if (limit !== undefined) {
                                    setPaymentTokenLimit(limit);
                                    setValidPaymentCount(true);
                                  }
                                }
                                formState.setFieldValue('paymentType', value);
                              }}
                            />
                            {/*{formState.values.paymentType &&*/}
                            {/*  formState.values.expiryDate && (*/}
                            {/*    <div className="d-flex justify-between mt_4">*/}
                            {/*      <div className="d-flex align-center">*/}
                            {/*        <ClockIcon />*/}
                            {/*        <p className="p p_font-size_14 ml_6">*/}
                            {/*          Предельный срок исполнения требований:{' '}*/}
                            {/*          {getFormatDate(*/}
                            {/*            new Date(formState.values.expiryDate),*/}
                            {/*            'dd.MM.yyyy',*/}
                            {/*            true*/}
                            {/*          )}*/}
                            {/*        </p>*/}
                            {/*      </div>*/}
                            {/*      <a*/}
                            {/*        className="p p_font-size_14 p_color_blue"*/}
                            {/*        href="/#"*/}
                            {/*      >*/}
                            {/*        Решение о выпуске*/}
                            {/*      </a>*/}
                            {/*    </div>*/}
                            {/*  )}*/}
                          </div>
                        </>
                      )}
                      {!formState.values.isFiat && paymentTokenLimit && (
                        <div className="form-assets__item form-assets__item__200">
                          <div className="form-item">
                            <UiInput
                              lg
                              mask={'###########'}
                              className="cfa-release__input"
                              placeholder="Количество токенов, передаваемых за выпуск"
                              onChange={(
                                e: React.FormEvent<HTMLInputElement>
                              ) => {
                                !Number.isNaN(
                                  Number(
                                    e.currentTarget.value.replace(/\s/g, '')
                                  )
                                ) &&
                                  formState.setFieldValue(
                                    'paymentCount',
                                    e.currentTarget.value
                                  );
                                let count = Number(
                                  e.currentTarget.value.replace(/\s/g, '')
                                );
                                if (
                                  paymentTokenLimit !== undefined &&
                                  count !== undefined
                                ) {
                                  if (paymentTokenLimit > count) {
                                    setValidPaymentCount(true);
                                  } else {
                                    setValidPaymentCount(false);
                                  }
                                }
                              }}
                              error={!validPaymentCount}
                            />
                            {!validPaymentCount && (
                              <div className="form-item__footer">
                                <span className="red">
                                  Значение больше, чем общее число токенов в
                                  эмиссии
                                </span>
                                <br />
                                <span className="red">
                                  Общая сумма эмиссии: {paymentTokenLimit}{' '}
                                  токенов
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/*<label className="form-label">*/}
                      {/*  <AppCheckbox*/}
                      {/*    onChangeValue={(val) => {*/}
                      {/*      formState.setFieldValue('isProvision', val);*/}
                      {/*    }}*/}
                      {/*  />*/}
                      {/*  При использовании имущественного обеспечения*/}
                      {/*</label>*/}

                      {/*{formState.values.isProvision && (*/}
                      {/*  <UiTextarea*/}
                      {/*    placeholder="Сведения об обеспечении выпускаемых ЦФА"*/}
                      {/*    onChange={(val) => {*/}
                      {/*      formState.setFieldValue(*/}
                      {/*        'provisionText',*/}
                      {/*        val.currentTarget.value*/}
                      {/*      );*/}
                      {/*    }}*/}
                      {/*  />*/}
                      {/*)}*/}

                      {/*<label className="form-label">*/}
                      {/*  <AppCheckbox*/}
                      {/*    onChangeValue={(val) => {*/}
                      {/*      formState.setFieldValue('isLiability', val);*/}
                      {/*    }}*/}
                      {/*  />*/}
                      {/*  Ограничение ответственности*/}
                      {/*</label>*/}

                      {/*{formState.values.isLiability && (*/}
                      {/*  <UiTextarea*/}
                      {/*    placeholder="Сведения об ограничении ответственности"*/}
                      {/*    onChange={(val) => {*/}
                      {/*      formState.setFieldValue(*/}
                      {/*        'liabilityText',*/}
                      {/*        val.currentTarget.value*/}
                      {/*      );*/}
                      {/*    }}*/}
                      {/*  />*/}
                      {/*)}*/}

                      <UiTextarea
                        placeholder="Иные сведения"
                        onChange={(val) => {
                          formState.setFieldValue(
                            'comment',
                            val.currentTarget.value
                          );
                        }}
                      />

                      <FormFooter>
                        <div className="flex justify-end">
                          {formState.values?.claim &&
                            formState.values?.companyName && (
                              <div className="form-assets__flex">
                                <div className="form-assets__item form-assets__item--sm">
                                  <ModuleTitleText
                                    top={
                                      'Вы формируете решение о выпуске ЦФА для ' +
                                      formState.values.companyName +
                                      ' на сумму:'
                                    }
                                    bottom={
                                      formState.values.claim &&
                                      currencyFormat(
                                        Number(
                                          formState.values.claim.replace(
                                            /\s/g,
                                            ''
                                          )
                                        )
                                      )
                                    }
                                  />
                                </div>
                              </div>
                            )}
                          <IconBtn
                            disabled={
                              loadingBtn ||
                              (!formState.values.claim &&
                                formState.values.isFiat) ||
                              (!formState.values.tokenCount &&
                                !formState.values.isFiat) ||
                              (!formState.values.companyId &&
                                !formState.values.publicRelease) ||
                              !formState.values.paymentType ||
                              // !formState.values.minDate ||
                              !formState.values.expiryDate ||
                              !formState.isValid
                            }
                            onClick={() => {
                              formState.handleSubmit();
                            }}
                            icon={<ReleaseDocumentIcon color="#fff" />}
                            title="Сформировать решение о выпуске ЦФА"
                          />
                        </div>
                      </FormFooter>
                    </form>
                  </div>
                </div>
              </div>
            </TableWrapper>
          </div>
        </div>
      </div>
    </AppPageMain>
  );
};

export default CfaReleasePage;
